import type { ReactNode } from "react";
import { Text } from "~/components/text";
import { Checkbox } from "~/components/ui/checkbox";
import { cn } from "~/utils";
import {
  type BisacFacetItem,
  type BisacFilterSubject,
  getBisacSubjectLabel,
  stripSubjectHeading,
} from "./bisac-filter-items";

interface BisacCodeListProps {
  name: string;
  selectedValues: string[];
  onToggle: (value: string, checked: boolean) => void;
}

function BisacCodeFilterRow({
  item,
  subjectLabel,
  name,
  checked,
  onCheckedChange,
}: {
  item: BisacFacetItem;
  subjectLabel: string;
  name: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const rowId = `checkbox-filter-${name}-${item.value}`;

  return (
    <label
      htmlFor={rowId}
      data-testid={`checkbox-filter-${item.value}`}
      title={item.label}
      className={cn(
        "flex items-start gap-3 px-6 py-1.5 cursor-pointer transition-colors",
        checked ? "bg-primary-25" : "hover:bg-secondary-100",
      )}
    >
      <Checkbox
        checked={checked}
        id={rowId}
        className="ml-1 mt-0.5 shrink-0"
        // Keep focus on the checkbox so keyboard users can toggle it with Space.
        onMouseDown={(event) => {
          event.stopPropagation();
          event.preventDefault();
        }}
        onCheckedChange={(nextChecked) => onCheckedChange(Boolean(nextChecked))}
      />
      <Text
        as="span"
        variant="captionStrong"
        numeric="mono"
        className="w-[84px] shrink-0 text-[13px] leading-5"
      >
        {item.value}
      </Text>
      <Text as="span" variant="body" className="min-w-0 flex-1 break-words">
        {stripSubjectHeading(item, subjectLabel)}
      </Text>
    </label>
  );
}

function BisacCodeGroup({
  heading,
  count,
  testId,
  children,
}: {
  heading: string;
  count: number;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div role="group" aria-label={heading} data-testid={testId}>
      <div className="sticky top-0 z-10 flex items-center border-b border-secondary-100 bg-white px-6 py-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-secondary-500">
        <span className="min-w-0 flex-1 truncate">{heading}</span>
        <span
          className="shrink-0 pl-2 tabular-nums"
          data-testid="bisac-group-count"
        >
          {count}
        </span>
      </div>
      {children}
    </div>
  );
}

export function BisacCodeFilterGroups({
  subjects,
  codeCounts,
  name,
  selectedValues,
  onToggle,
}: BisacCodeListProps & {
  subjects: BisacFilterSubject[];
  codeCounts: Map<string, number>;
}) {
  return subjects.map((subject) => (
    <BisacCodeGroup
      key={subject.code}
      heading={subject.label}
      count={codeCounts.get(subject.code) ?? subject.items.length}
      testId={`bisac-filter-group-${subject.code}`}
    >
      {subject.items.map((item) => (
        <BisacCodeFilterRow
          key={item.value}
          item={item}
          subjectLabel={subject.label}
          name={name}
          checked={selectedValues.includes(item.value)}
          onCheckedChange={(checked) => onToggle(item.value, checked)}
        />
      ))}
    </BisacCodeGroup>
  ));
}

export function BisacSelectedCodes({
  items,
  name,
  selectedValues,
  onToggle,
}: BisacCodeListProps & { items: BisacFacetItem[] }) {
  return items.map((item) => (
    <BisacCodeFilterRow
      key={item.value}
      item={item}
      subjectLabel={getBisacSubjectLabel(item)}
      name={name}
      checked={selectedValues.includes(item.value)}
      onCheckedChange={(checked) => onToggle(item.value, checked)}
    />
  ));
}
